import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {BBS_CATEGORY} from "../shared/defines/protocol";
import {Article} from "../shared/models/article.model";
import {BBS_INFO, HISTORY_KEY_SEARCH_BOARD, SearchParam} from "../shared/defines/client.const";
import {ARTICLE_PAGE_SIZE, MENU_PAGE} from '../shared/defines/desktop.const';
import {IsEqualObj} from "../shared/defines/common.func";
import {PageLoader} from "../shared/interfaces/page-loader";
import {BoardService} from "../shared/services/board/board.service";
import {GlobalEventService} from "../shared/services/global-event/global-event.service";
import {buildDynamicDescription, normalizeForMeta, SeoService} from "../shared/services/seo/seo.service";


/**
 * 게시판 카테고리와 메뉴 페이지를 연결하는 맵
 */
const BBS_CATEGORY_MENU_MAP: Map<BBS_CATEGORY, MENU_PAGE> = new Map([
  [BBS_CATEGORY.NOTICE, MENU_PAGE.BBS_NOTICE],
  [BBS_CATEGORY.USAGE, MENU_PAGE.BBS_USAGE],
]);


@Component({
  selector: 'app-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.scss']
})
export class BoardComponent implements OnInit, OnDestroy {
  // @ViewChild('main', { static: true }) main?: ElementRef; // ngOnInit() 에서 사용 가능

  protected selectedMenu = MENU_PAGE.BBS_USAGE;
  protected readonly HISTORY_KEY_SEARCH_BOARD = HISTORY_KEY_SEARCH_BOARD;

  private category: BBS_CATEGORY | null = null; // 현재 파라미터로 받아온 게시판 범주(게시판 구분자)
  private articleId: number | null = null; // 경로 파라미터로 받아온 게시글 번호
  private inited = false;

  // 검색창에 넣어주는 값
  public placeholder: string | null = null;
  public keyword: string | null = null;
  protected scrollTop = true;

  // 라우터에서 넣어주는 값. (TODO: 초기화에 유의해야 함)
  private queryParams: SearchParam | null = null;
  private listParams: SearchParam | null = null;

  protected leftArticle: Article | null = null;
  protected rightArticle: Article | null = null;

  // PageLoader 설정
  public pl: PageLoader<Article, number> | null = null;
  public get ListLeft(): Array<Article> | null {
    return this.listHalfIndex ? (this.pl?.getList()?.slice(0, this.listHalfIndex) ?? null) : null;
  }
  public get ListRight(): Array<Article> | null {
    return this.listHalfIndex ? (this.pl?.getList()?.slice(this.listHalfIndex) ?? null) : null;
  }

  private queryParams$?: Subscription;
  private params$?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private boardSvc: BoardService,
    private globalEventSvc: GlobalEventService,
    private seoSvc: SeoService,
  ) {
    // super(globalEventSvc);

    /******************************************************************************************
     * 주소 뒤의 파라미터를 받아오는 곳.
     * 입력된 파라미터가 없어도 처음 초기화 할 때에도 불려진다.
     * router.navigate()로 같은 주소를 호출하면 이 코드가 다시 불려진다.
     */
    this.queryParams$ = this.route.queryParams.subscribe(params => {
      console.warn('BoardComponent::constructor() - queryParams');
      this.queryParams = {...params};

      this.keyword = this.queryParams?.keyword ?? null;

      if (this.inited) { // 이미 초기화가 되어있는 경우.
        this.init();
      }

    });

    /******************************************************************************************
     * 라우터 파라미터를 읽어오는 곳
     * 처음 이 페이지에 들어올 때 불려진다.
     * url이 변경되어도 불린다.
     * queryParams가 바뀐 것(주소뒤의 파리미터가 변경된 경우)으로는 여기가 불려지지 않는다.
     */
    this.params$ = this.route.params.subscribe(params => {

      // cat 은 URL 에 포함된 파라미터라서 여기서 읽는다.
      if ('cat' in params) {
        const category: BBS_CATEGORY = parseInt(params['cat']) as BBS_CATEGORY;
        console.log('BoardComponent::constructor() - category', this.category, category, params);

        this.category = category;

        this.selectedMenu = BBS_CATEGORY_MENU_MAP.get(category) ?? MENU_PAGE.BBS_USAGE;
        console.warn('BoardComponent::constructor()', category, BBS_CATEGORY_MENU_MAP.get(category), this.selectedMenu);
      }
      else {
        console.error('BoardComponent::constructor() - category is not found.');
      }

      // id 는 경로 파라미터로 읽는다. (선택적)
      this.articleId = 'id' in params ? parseInt(params['id']) : null;
      if (this.articleId === null) {
        this.CloseArticle();
      }

      this.init();
      this.inited = true;
    });
  }

  ngOnInit(): void {
    // if (this.main) {
    //   this.InitFocusMain(this.main);
    // }
    // else {
    //   console.error('BoardComponent::ngOnInit() main is null');
    // }
  }

  ngOnDestroy(): void {
    // this.ExitFocusMain();
    this.queryParams$?.unsubscribe();
    this.params$?.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * init()
   *
   * TODO: 이 함수의 정의를 좀 더 명확히 해야 함.
   *
   */
  private init(): void {
    console.log('BoardComponent::init()', this.inited, this.category, this.queryParams);

    if (this.category !== null) {
      this.pl = this.boardSvc;
      this.placeholder = `${BBS_INFO[this.category].placeholder} 내에서 검색 합니다.`;
      this.keyword = this.queryParams?.keyword ?? null;

      let param: SearchParam = {
        bbs_cat: this.category,
        ...(this.queryParams?.page ? {page: this.queryParams?.page} : {}),
        ...(this.queryParams?.keyword ? {keyword: this.queryParams?.keyword} : {}),
      }

      if (this.listParams === null || !IsEqualObj(param, this.listParams)) {
        this.ReloadList(param);
        this.listParams = param;
      }
      else {
        this.ifOpenArticle();
      }
    }
  }


  protected onSearch(param: SearchParam): void {
    console.log('BoardComponent::onSearch()', param);

    this.router.navigate(['/board', this.category], {
      queryParams: {...param, bbs_cat: this.category},
    }).then();
  }

  protected goNextPage($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.CloseArticle();

    const isNext = this.pl?.IsLastPage() ?? true;
    if (!isNext) {
      const queryParams: SearchParam = {
        ...this.queryParams,
        page: (this.pl?.getCurPage() ?? 0) + 1,
      };
      console.warn('BoardComponent::goNextPage() queryParams', queryParams);
      this.router.navigate(['/board', this.category], {queryParams}).then();
    }
  }

  protected  goPrevPage($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.globalEventSvc.Click($event, true);

    this.CloseArticle();

    const isPre = this.pl?.IsFirstPage() ?? true;
    if (!isPre) {
      const queryParams: SearchParam = {
        ...this.queryParams,
        page: (this.pl?.getCurPage() ?? 0) - 1,
      };
      this.router.navigate(['/board', this.category], {queryParams}).then();
    }
  }

  private ReloadList(param?: SearchParam) {
    console.log('BoardComponent::ReloadList()', param, this.queryParams);

    // this.lm.Reset();
    if (this.pl === null) {
      throw new Error('BoardComponent::ReloadList() - this.pl is null.');
    }

    this.pl.ResetList(param).subscribe(page => {
      this.pl?.GetNextList().pipe(
        takeUntil(this.destroy$)
      ).subscribe((list: Array<Article>) => {
        if (this.queryParams === null) {
          throw new Error('BoardComponent::ReloadList() - this.queryParams is null.');
        }

        this.ifOpenArticle();
        console.log('ArticleComponent::ReloadList:: ll, list', this.pl, list);

        // 목록이 로드된 후 첫 번째 항목을 엽니다. (단, id가 지정되지 않은 경우)
        // TODO: 나중에 오른쪽 목록이 생기면, 삭제해야 함.
        const shouldAutoOpen = list.length > 0 && this.articleId === null;
        if (shouldAutoOpen) {
          const firstArticle = list[0];
          this.onSelectArticle(firstArticle.no);
        }
      });
    });
  }

  private ifOpenArticle(): void {
    console.warn('BoardComponent::ifOpenArticle()', this.articleId);
    if (this.articleId !== null) {
      const article_no = this.articleId;
      if (this.ListLeft?.find(article => article.no === article_no)){
        this.OpenLeft(article_no);
      }
      else if (this.ListRight?.find(article => article.no === article_no)) {
        this.OpenRight(article_no);
      }
      else {
        throw new Error(`BoardComponent::ReloadList() - article_no(${article_no}) is not found.`);
      }
    }
  }

  protected onSelectArticle(article_no: number) {
    const selectedArticle = this.findArticleByNo(article_no);
    if (selectedArticle) {
      this.updateArticleSeo(selectedArticle);
    }

    this.router.navigate(['/board', this.category, article_no], {
      queryParams: this.queryParams,
    }).then();
  }

  protected onCloseArticle(article_no: number) {
    this.router.navigate(['/board', this.category], {
      queryParams: this.queryParams,
    }).then();
  }

  protected OpenLeft(article_no: number) {
    console.log('BoardComponent::OpenLeft()', article_no);

    this.leftArticle = this.ListLeft?.find(article => article.no === article_no) ?? null;
    if (this.leftArticle === null) {
      console.error(`BoardComponent::OpenLeft() - article_no(${article_no}) is not found.`);
      this.seoSvc.updateByUrl(this.router.url);
    }
    else {
      this.updateArticleSeo(this.leftArticle);
    }
    this.rightArticle = null;
  }

  protected OpenRight(article_no: number) {
    console.log('BoardComponent::OpenRight()', article_no);

    this.rightArticle = this.ListRight?.find(article => article.no === article_no) ?? null;
    if (this.rightArticle === null) {
      console.error(`BoardComponent::OpenRight() - article_no(${article_no}) is not found.`);
      this.seoSvc.updateByUrl(this.router.url);
    }
    else {
      this.updateArticleSeo(this.rightArticle);
    }
    this.leftArticle = null;
  }

  protected CloseArticle() {
    this.leftArticle = null;
    this.rightArticle = null;
    this.seoSvc.updateByUrl(this.router.url);
  }

  public GetNextBtnCss(): string {
    return this.pl?.IsLoading() ? 'loading' : this.pl?.IsLastPage() ? '' : 'on';
  }

  public GetPreBtnCss(): string {
    return this.pl?.IsLoading() ? 'loading' : this.pl?.IsFirstPage() ? '' : 'on';
  }



  private get listHalfIndex(): number | null {
    const sizeList = this.pl?.getList()?.length;
    // return sizeList ? GetCeilHalf(sizeList) : null;
    return sizeList ? Math.ceil(ARTICLE_PAGE_SIZE / 2) : null;
  }


  protected onScrollTop($event: Event): void {
    const target = $event.target as HTMLElement;

    // console.log('onScrollTop() ', target.scrollTop);
    this.scrollTop = target.scrollTop === 0; // 수직 스크롤 위치
  }

  private findArticleByNo(article_no: number): Article | null {
    return this.ListLeft?.find(article => article.no === article_no)
      ?? this.ListRight?.find(article => article.no === article_no)
      ?? null;
  }

  private updateArticleSeo(article: Article): void {
    this.seoSvc.updateDynamic({
      pageTitle: normalizeForMeta(article.title),
      description: buildDynamicDescription(article.brief, article.content),
      url: this.getArticleUrl(article.no),
    });
  }

  private getArticleUrl(article_no: number): string {
    return this.category === null ? this.router.url : `/board/${this.category}/${article_no}`;
  }
}
